// SYSTEM_ROUTE: /capture
import { useState } from 'react';
import type { CaptureType, EntityId } from '@life-os/app-core';
import { Button, CompactRow, EmptyState, Pane, PrivacyChip, Stack } from '@life-os/ui';
import { useServices, useStore } from '../app/store.js';
import { ScreenHeader } from '../app/ScreenHeader.js';
import { useAsync } from '../lib/useAsync.js';

const TYPES: CaptureType[] = [
  'unspecified',
  'task',
  'note',
  'source',
  'care_commitment',
  'relationship_note',
  'value_or_why',
  'quality_of_life_motive',
];

export function CaptureScreen() {
  const services = useServices();
  const { revision, refresh } = useStore();
  const [text, setText] = useState('');
  const [type, setType] = useState<CaptureType>('unspecified');

  const unreviewed = useAsync(() => services.capture.listUnreviewed(), [revision]);

  const save = async () => {
    if (text.trim().length === 0) return;
    await services.capture.captureItem({ rawText: text, captureType: type });
    setText('');
    setType('unspecified');
    refresh();
  };

  const triage = async (id: EntityId, kind: 'promote_to_task' | 'keep_as_note' | 'archive') => {
    if (kind === 'promote_to_task') {
      await services.capture.reviewCapture(id, { kind, taskId: '' as EntityId });
    } else {
      await services.capture.reviewCapture(id, { kind });
    }
    refresh();
  };

  const items = unreviewed.value ?? [];

  return (
    <>
      <ScreenHeader title="Capture" subtitle="Record anything without deciding its structure yet." />

      <Pane title="New capture">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="What's on your mind?"
          rows={3}
          style={{
            width: '100%',
            resize: 'vertical',
            background: 'var(--lc-surface-container)',
            color: 'var(--lc-on-surface)',
            border: '1px solid var(--lc-outline-variant)',
            borderRadius: 'var(--lc-radius-lg)',
            padding: '10px 12px',
            font: 'inherit',
            fontSize: 'var(--lc-text-sm)',
          }}
        />
        <div style={{ display: 'flex', gap: 'var(--lc-stack-sm)', alignItems: 'center' }}>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as CaptureType)}
            aria-label="Capture type (optional)"
            style={{
              background: 'var(--lc-surface-container)',
              color: 'var(--lc-on-surface)',
              border: '1px solid var(--lc-outline-variant)',
              borderRadius: 'var(--lc-radius-full)',
              padding: '6px 12px',
              font: 'inherit',
              fontSize: 'var(--lc-text-sm)',
            }}
          >
            {TYPES.map((t) => (
              <option key={t} value={t}>
                {t.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
          <span className="page-sub">Classification is optional — you can decide later.</span>
          <Button variant="primary" style={{ marginLeft: 'auto' }} onClick={() => void save()}>
            Save
          </Button>
        </div>
      </Pane>

      <Pane title="Inbox" hint={`${items.length} item(s) awaiting review`}>
        <Stack>
          {items.length === 0 ? (
            <EmptyState title="Inbox is clear">Nothing to triage. Capture something above when it comes up.</EmptyState>
          ) : (
            items.map((c) => (
              <CompactRow
                key={String(c.id)}
                title={c.rawText}
                subtitle={`${c.captureType.replace(/_/g, ' ')} · captured`}
                meta={
                  <>
                    <PrivacyChip value={c.privacyClass} />
                    <Button variant="ghost" onClick={() => void triage(c.id, 'promote_to_task')}>
                      → Task
                    </Button>
                    <Button variant="ghost" onClick={() => void triage(c.id, 'keep_as_note')}>
                      Keep
                    </Button>
                    <Button variant="ghost" onClick={() => void triage(c.id, 'archive')}>
                      Archive
                    </Button>
                  </>
                }
              />
            ))
          )}
        </Stack>
      </Pane>
    </>
  );
}
